import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { toast } from 'react-toastify';
import CorrelationChartThemeManager from 'src/managers/themeManager/correlationThemeManager';
import { TREE_MAP } from 'src/components/Charts/types';
import { isNullOrEmptyArray } from '../../../util/arrays';
import { Card, CorrelationChart, CardMinzd } from '../../../components';
import {
  fetchCorrelationChartData,
  fetchAutoCorrelationChartData,
  fetchCorrelationChartColorConfigData,
} from '../../../stores/actions/charts';
import {
  fetchCorrelationConfigs,
  removeCorrConfig,
} from '../../../stores/actions/configs';
import { useErrorToast } from '../../../hooks';
import { cardsMap } from '../cardsMap';
import { graphTypes } from '../types';
import * as Ui from '../styles';
import CorrelationConfig from './config/CorrelationConfig';
import { filterByKeywords } from './textSearch';
import { annotationStrategyMap } from '../../../stores/actions/annotations';
import ContextualCardAnnotations from '../../ContextualCardAnnotations';

// mensagem para dados vazios
const msgIsNullOrEmptyDataChart = 'As correlações estão fora dos intervalos definidos na sua nota de corte';
const msgInitialEmptyChart = 'Crie uma configuração para visualizar seus resultados';

/**
 * extract title by profile features
 */
function extractTitleChart(data) {
  if (data && data.alternativas && data.alternativas.length > 0) {
    return data.alternativas.map((a) => ({
      label: a.enunciado,
      title: 'Alternativa da pergunta principal',
    }));
  }
  return [];
}

function CorrelationChartCard({
  /**
   * flag indicando se o card da correlação deve ser exibido em fullScreen ou não
   */
  isFullScreen,
  /**
   * flag indicando se existe algum card em full screen
   */
  isSomeoneInFullScreen,
  /**
   * callback chamado quando existe uma interação com o botão toggle de fullscreen
   */
  onFullScreenToggle,
  /**
   * outras propriedas
   */
  ...props
}) {
  // props from redux
  const { dispatch, configStore, chartStore } = props;
  // props from them
  const { theme } = props;
  // props from parent
  const { customerId, surveyId, questionId, questionTitle } = props;
  // state
  const [waiting, setWaiting] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [withData, setWithData] = useState(false);
  const [profileText, setProfileText] = useState(null);
  const [presets, setPresets] = useState([
    /* {value, label} */
  ]);
  const [correlationChartType, setCorrelationChartType] = useState('simple_bubble');
  const [filter, setFilter] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [selectedCorrelationConfigId, setSelectedCorrelationConfigId] = useState(null);
  const [
    latestSelectedCorrelationConfigId,
    setLatestSelectedCorrelationConfigId,
  ] = useState(null);
  const [isAutoCorr, setIsAutoCorr] = useState(false);
  const [editableConfigId, setEditableConfigId] = useState(null);
  const [isMinimizedScreen, setMinimizeScreen] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);

  // hooks
  const [checkError] = useErrorToast();
  // title
  const title = isAutoCorr ? 'Correlação Automática' : 'Correlação Manual';
  // refs
  const cardRef = useRef();
  const chartRef = React.createRef(null);

  // analyzeId efetivo para o ThemeManager: questionId no modo automático, configurationId
  // no manual. A action de persistência usa esse id + a flag isAuto para escolher entre os
  // chartTypes `correlationAutoChart` e `correlationManualChart` na URL.
  const effectiveAnalyzeId = isAutoCorr ? questionId : selectedCorrelationConfigId;

  // Gerenciador de tema do card de correlação. Reusa o pipeline padrão de ThemeManager,
  // só sobrescrevendo as actions para carregar a flag isAuto. O `chartType` define a
  // estratégia de useSeries (todos os 3 tipos da correlação compartilham o mesmo formato
  // de série canônica — lista de perguntas correlacionadas em `option.legend.data`).
  const themeManager = new CorrelationChartThemeManager(
    effectiveAnalyzeId,
    surveyId,
    withData,
    chartRef,
    theme,
    isAutoCorr,
    correlationChartType,
  );

  const {
    colorPickerColors,
    configurationError,
    fontsSizes,
    colorSchemeValue,
  } = themeManager.getConfig();

  // gancho: ao mudar o id efetivo da correlação selecionada (auto ou manual), carrega
  // as configurações de cores correspondentes do backend.
  useEffect(() => {
    if (!surveyId || !effectiveAnalyzeId) return;
    fetchCorrelationChartColorConfigData(
      surveyId,
      effectiveAnalyzeId,
      isAutoCorr,
      dispatch,
    );
  }, [surveyId, effectiveAnalyzeId, isAutoCorr, dispatch]);

  // resetPresets reinicializa os dados da configuração
  const resetPresets = useCallback(() => {
    setPresets([]);
    setSelectedCorrelationConfigId(null);
  }, []);
  // refreshConfigs atualiza a listagem de configurações
  const refreshConfigs = useCallback(() => {
    if (surveyId && customerId && questionId) {
      resetPresets();
      fetchCorrelationConfigs(surveyId, customerId, questionId, dispatch);
    }
  }, [surveyId, customerId, questionId, resetPresets, dispatch]);
  // resetData reinicializa os dados do gráfico
  const resetData = useCallback(() => {
    setChartData(null);
    setProfileText(null);
    setWithData(false);
  }, []);
  // refreshConfigs atualiza a listagem de configurações
  const refreshAutoCorr = useCallback(() => {
    if (surveyId && customerId && questionId) {
      fetchAutoCorrelationChartData(customerId, surveyId, questionId, dispatch);
      setWaiting(true);
    }
  }, [surveyId, customerId, questionId, dispatch]);

  // gancho: quando a triade principal (customerId, surveyId, questionId) alterar
  // efeito: listar todas as configurações para a triade principal
  useEffect(() => {
    refreshConfigs();
  }, [refreshConfigs]);

  // gancho: alteração da listagem de configurações
  // efeito: caso não exista preset definido, definir com os dados da store
  useEffect(() => {
    if (!isNullOrEmptyArray(configStore.data) && isNullOrEmptyArray(presets)) {
      setPresets(
        configStore.data.map((item) => ({
          label: item.titulo,
          value: item.id,
        })),
      );
    }
  }, [configStore, presets]);

  // gancho: alteração da listagem de configurações
  // efeito: selecionar o último item selecionado ou 0
  useEffect(() => {
    if (!isNullOrEmptyArray(presets) && cardRef.current) {
      // if a configuration was selected before
      if (latestSelectedCorrelationConfigId) {
        // find configId
        const foundIndex = presets.findIndex(
          (p) => p.value === latestSelectedCorrelationConfigId,
        );
        // if exists select
        if (foundIndex !== -1) {
          setTimeout(
            () => cardRef.current.selectPresetItemValue(
              latestSelectedCorrelationConfigId,
            ),
            500,
          );
        } else {
          setLatestSelectedCorrelationConfigId(null);
        }
      }
    }
  }, [presets, cardRef, latestSelectedCorrelationConfigId]);

  // gancho: alteração do erro
  // efeito: caso haja um erro, apresentar uma mensagem
  useEffect(() => {
    // get error
    const error = (chartStore && chartStore.error) || (configStore && configStore.error);
    // show error if exists
    if (error && waiting) {
      checkError(error);
    }
    // stop waiting
    setWaiting(false);
  }, [chartStore, checkError, configStore, waiting]);

  // gancho: alteração na identidade da configuração
  // efeito: solicitar a listagem de dados do gráfico a partir do id da condiguração
  useEffect(() => {
    if (customerId && selectedCorrelationConfigId && surveyId) {
      setIsAutoCorr(false);
      fetchCorrelationChartData(
        customerId,
        surveyId,
        selectedCorrelationConfigId,
        dispatch,
      );
    }
  }, [customerId, selectedCorrelationConfigId, surveyId, dispatch]);

  // ao modificar o estado dos dados de plotagem
  useEffect(() => {
    // check if has data
    const hasData = chartStore
      && chartStore.data
      && !isNullOrEmptyArray(chartStore.data.alternativas)
      && (selectedCorrelationConfigId || isAutoCorr);
    // set values
    if (hasData) {
      setChartData(chartStore.data);
      setProfileText(extractTitleChart(chartStore.data));
      setWithData(true);
    } else {
      resetData();
    }
    // stop waiting
    setWaiting(false);
  }, [chartStore, isAutoCorr, resetData, selectedCorrelationConfigId]);

  // limpar os dados quando a questão for alterada
  useEffect(() => {
    resetData();
    resetPresets();
  }, [questionId, resetData, resetPresets]);

  // quando os dados de pesquisa e da questão for definido
  useEffect(() => {
    setIsAutoCorr(true);
    refreshAutoCorr();
  }, [refreshAutoCorr]);

  // quando altera o tipo de consulta para auto ou manual
  useEffect(() => {
    if (chartStore.type === 'auto') setCorrelationChartType(TREE_MAP);
  }, [chartStore.type]);

  // handle open config
  function handleOpenConfig() {
    setShowConfig(true);
  }

  // handle close config
  function handleCloseConfig() {
    setShowConfig(false);
    setEditableConfigId(null);
  }

  // handle on save config
  function handleSaveConfig() {
    handleCloseConfig();
    refreshConfigs();
    if (isAutoCorr) {
      refreshAutoCorr();
    }
  }

  // handle remove action of a configuration
  function handleRemoveConfig(configId) {
    removeCorrConfig(customerId, surveyId, questionId, configId, dispatch);
    refreshConfigs();
  }

  // handle on edit config (card)
  function handleEditConfig(configId) {
    setEditableConfigId(configId);
    setShowConfig(true);
  }

  // handle configuration selection
  function handleSelectedConfig(configId) {
    setSelectedCorrelationConfigId(configId);
    setLatestSelectedCorrelationConfigId(configId);
  }

  // handle full screen toggle
  function handleFullScreenToggle(cardId, isFull) {
    if (onFullScreenToggle) onFullScreenToggle(cardId, isFull);
  }

  // handle on change chart type
  function handleTypeChange(newType) {
    setCorrelationChartType(newType);
  }

  // handle color scheme type change (padrão/neutro)
  function handleColorSchemeChange(newType) {
    themeManager.updateColorScheme(newType);
  }

  // handle chart font type change
  function handleChartFontChange(newFontsSizes) {
    themeManager.updateFontSize(newFontsSizes);
  }

  // handle change filter value
  function handleChangeFilterValue(text) {
    setFilter(text);
  }

  // manipula a solicitação de minimizar o card
  function handleMinimize() {
    setMinimizeScreen(true);
  }

  function handleCloseAnnotations() {
    setShowAnnotations(false);
  }

  function handleAnnotationsCancel() {
    handleCloseAnnotations();
  }

  function handleOpenAnnotations() {
    setShowAnnotations(true);
  }

  function handleAnnotationsSave() {
    setShowAnnotations(false);
  }

  function handleAnnotationsFailure(errorType) {
    const errorStrategy = {
      fetch: () => {
        handleCloseAnnotations();
        const message = 'Houve uma falha ao recuperar sua anotação, tente novamente';
        toast.error(message);
      },
      save: () => {
        const message = 'Não foi possível salvar sua anotação, tente novamente';
        toast.error(message);
      },
    };
    errorStrategy[errorType]();
  }

  // for render configurations
  // menu-structure: {type: [<charttypes>|<colorscheme>|<chartfonts>|<configbtn>|<annotations>]}
  function buildMenu() {
    const graphTypeName = chartStore.type === 'auto' ? 'correlacaoAuto' : 'correlacao';
    const graphTypeConfig = graphTypes.get(graphTypeName) || {};

    const types = graphTypeConfig.type || [];
    const selectedTypeIndex = types.findIndex(
      (t) => t.value === correlationChartType,
    );

    const colorSchemeTypes = graphTypeConfig.colorSchemeType || [];
    const selectedColorSchemeTypeIndex = colorSchemeTypes.findIndex(
      (t) => t.value === colorSchemeValue,
    );

    const chartFontTypes = graphTypeConfig.chartFontsType || [];
    const selectedFontTypeIndex = chartFontTypes.findIndex(
      (t) => t.value === fontsSizes,
    );

    const chartTypesOption = {
      type: 'charttypes',
      selectedIndex: selectedTypeIndex,
      data: types,
      onSelect: handleTypeChange,
    };

    const colorSchemeOption = {
      type: 'colorscheme',
      selectedIndex: selectedColorSchemeTypeIndex,
      data: colorSchemeTypes,
      onSelect: handleColorSchemeChange,
    };

    const chartFontsOption = {
      type: 'chartfonts',
      selectedIndex: selectedFontTypeIndex,
      data: chartFontTypes,
      onSelect: handleChartFontChange,
    };

    const annotationsOption = {
      type: 'annotations',
      onClick: handleOpenAnnotations,
    };

    const configurationOption = {
      type: 'configbtn',
      onClick: handleOpenConfig,
      title: 'Configurar correlação...',
    };

    // Sem dado plotado, mostramos só o seletor de tipo + esquema/fonte
    // (mantém o controle do usuário sem depender de um resultado já carregado).
    if (!chartData) {
      return [
        chartTypesOption,
        colorSchemeOption,
        chartFontsOption,
      ].map((option, index) => ({ ...option, key: `key#${index}` }));
    }

    const options = [
      chartTypesOption,
      annotationsOption,
      colorSchemeOption,
      chartFontsOption,
      configurationOption,
    ];

    return options.map((option, index) => ({ ...option, key: `key#${index}` }));
  }

  function renderAnnotations() {
    const strategy = isAutoCorr
      ? annotationStrategyMap.correlationAuto
      : annotationStrategyMap.correlation;
    const resultKey = isAutoCorr ? questionId : selectedCorrelationConfigId;

    return showAnnotations ? (
      <ContextualCardAnnotations
        strategy={strategy}
        customerId={customerId}
        surveyId={surveyId}
        resultKey={resultKey}
        onCancel={handleAnnotationsCancel}
        onSave={handleAnnotationsSave}
        onFailure={handleAnnotationsFailure}
      />
    ) : null;
  }

  function renderChartCard() {
    return isMinimizedScreen ? (
      <CardMinzd title={title} onRestore={() => setMinimizeScreen(false)} />
    ) : (
      <Card
        minimizable
        withPresets
        withEditablePreset
        withRemovablePreset
        ref={cardRef}
        cardId={cardsMap.correlacao.cardId}
        cardTitle={title}
        cardTooltip={'Visualize as relações estatísticas automaticamente identificadas entre as variáveis.'}
        questionTitle={questionTitle}
        cardTitleCompl={profileText}
        isFullScreenMode={isFullScreen}
        menu={buildMenu()}
        withData={withData}
        loading={chartStore.running}
        presets={presets}
        emptyChartText={
          questionId ? msgIsNullOrEmptyDataChart : msgInitialEmptyChart
        }
        loadingPresets={configStore.running}
        onSelectPreset={(items) => handleSelectedConfig(items[0].value)}
        onRemovePreset={(item) => handleRemoveConfig(item.value)}
        onEditPreset={(item) => handleEditConfig(item.value)}
        onChangeFilterValue={handleChangeFilterValue}
        onMaximize={handleFullScreenToggle}
        onMinimize={handleMinimize}
        theme={theme}
      >
        <CorrelationChart
          type={correlationChartType}
          data={chartData}
          legendsFilter={filter}
          legendsFilterHandler={filterByKeywords}
          theme={theme.colors.charts}
          // Props consumidas por todos os 3 tipos da correlação integrados ao ColorPicker.
          updateChartColors={themeManager.updateChartColors}
          colorPickerColors={colorPickerColors}
          colors={themeManager.calculatedChartColorArray}
          chartFontSize={fontsSizes}
          error={configurationError}
          chartRef={chartRef}
        />
      </Card>
    );
  }

  function renderConfig() {
    return showConfig ? (
      <CorrelationConfig
        customerId={customerId}
        surveyId={surveyId}
        questionId={questionId}
        questionTitle={questionTitle}
        configId={editableConfigId}
        isOpen={showConfig}
        onClose={handleCloseConfig}
        onSave={handleSaveConfig}
      />
    ) : null;
  }

  return (
    <Ui.CardContainer
      className="cardContainer"
      cardIsFullScreen={isFullScreen}
      someoneInFullScreen={isSomeoneInFullScreen}
    >
      {/* chart cart */}
      {renderChartCard()}
      {/* modal */}
      {renderConfig()}
      {renderAnnotations()}
    </Ui.CardContainer>
  );
}

const configSelector = (state) => state.configs.correlationConfigs;
const chartSelector = (state) => state.charts.correlationChartData;
const mapStateToProps = (state) => {
  const all = createSelector(
    [configSelector, chartSelector],
    (configState, chartState) => ({
      configStore: {
        running: configState.running,
        error: configState.error,
        data: configState.payload,
      },
      chartStore: {
        running: chartState.running,
        error: chartState.error,
        data: chartState.payload,
        type: chartState.type,
      },
    }),
  );
  return all(state);
};

export default connect(mapStateToProps)(CorrelationChartCard);
